import { createMiddleware } from '@tanstack/react-start'
import { getRequestHeader } from '@tanstack/react-start/server'
import {
  HttpError,
  UNAUTHORIZED_PREFIX,
  isUnauthorizedError,
} from '@rapid-response/shared'
import { getAccessToken } from '~/lib/access-token'
import { triggerAuthRecovery } from '~/lib/auth-recovery'
import { authenticateRpcRequest } from './rpc-auth'

/**
 * Per-RPC auth middleware.
 *
 * - Client step: reads the OIDC access token from the in-memory holder
 *   (populated by `CurrentUserProvider`) and attaches it as
 *   `Authorization: Bearer <token>` on the outbound fetch. If the response
 *   surfaces a 401 (matched by the `UNAUTHORIZED_PREFIX` sentinel on the
 *   error message — `HttpError.status` is dropped by TanStack Start's
 *   error serializer), it triggers the auth-recovery callback that
 *   `AuthGate` registers, so the user is silently re-authed instead of
 *   landing on a generic error page.
 * - Server step: re-reads the header off the inbound request, verifies the
 *   JWT against Keycloak's JWKS, looks up the matching `users` row, and
 *   exposes the resolved `caller` to the handler via `ctx.context.caller`.
 *   Any 401 thrown here (or from a downstream handler) gets the
 *   `UNAUTHORIZED_PREFIX` sentinel before being re-thrown so the client
 *   middleware can identify it after seroval flattens the error.
 *
 * Usage:
 *
 *   export const createItem = createServerFn({ method: 'POST' })
 *     .middleware([rpcAuthMiddleware])
 *     .inputValidator((d: CreateItemInput) => d)
 *     .handler(async ({ data, context }) =>
 *       createItemHandler({ ...data, caller: context.caller }),
 *     )
 *
 * Handlers wrapped with this middleware MUST NOT accept `actingUserId` from
 * the input — the caller's identity is derived from the verified JWT only.
 */
export const rpcAuthMiddleware = createMiddleware({ type: 'function' })
  .client(async ({ next }) => {
    const token = getAccessToken()
    return runClientWithRecovery(() =>
      next({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    )
  })
  .server(async ({ next }) => {
    return runServerWithUnauthorizedTag(async () => {
      const header = getRequestHeader('authorization')
      const { user } = await authenticateRpcRequest(header)
      return next({ context: { caller: user } })
    })
  })

/**
 * Wrap the client-side `next()` call so a verifier 401 fires the silent
 * re-auth flow. Exported so the bootstrap middleware in `./users.ts` can
 * share the behaviour without duplicating the matcher.
 */
export async function runClientWithRecovery<T>(
  call: () => Promise<T>,
): Promise<T> {
  try {
    return await call()
  } catch (err) {
    if (err instanceof Error && isUnauthorizedError(err)) {
      triggerAuthRecovery()
    }
    throw err
  }
}

/**
 * Wrap a server-side middleware body so any 401 it throws (directly or via
 * a downstream handler's `unauthorized()` call) gets the sentinel prefix
 * required for the client middleware to identify it. 403s and other
 * statuses are left untouched — only 401 means "credentials are bad,
 * re-authenticate".
 */
export async function runServerWithUnauthorizedTag<T>(
  call: () => Promise<T>,
): Promise<T> {
  try {
    return await call()
  } catch (err) {
    if (
      err instanceof HttpError &&
      err.status === 401 &&
      !err.message.startsWith(UNAUTHORIZED_PREFIX)
    ) {
      throw new HttpError(401, `${UNAUTHORIZED_PREFIX} ${err.message}`)
    }
    throw err
  }
}
